import { Box } from "@mui/material";
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

import { useTheme } from "@/components/ui-v2";

interface TextureMapProps {
  url: string;
}

export const TextureMap = ({ url }: TextureMapProps) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const loader = new GLTFLoader();

    loader.load(url, (gltf) => {
      let baseColorTexture: THREE.Texture | null = null;
      let geometry: THREE.BufferGeometry | null = null;

      // Traverse the scene to find the mesh and get its material/geometry data.
      // This is a little hacky as we can't explicitly break out of the `traverse`
      // function (https://stackoverflow.com/questions/69760494/abort-object3d-traverse-in-three-js).
      // If we decide to add multi-object scenes in the future, we should probably
      // just generate the UV image on the backend and save it as an attachment, but that seems
      // like overkill right now.
      gltf.scene.traverse((node: THREE.Object3D) => {
        if (
          node instanceof THREE.Mesh &&
          node.material &&
          node.material.map &&
          !baseColorTexture
        ) {
          baseColorTexture = node.material.map;
          geometry = node.geometry;
        }
      });

      if (baseColorTexture && geometry) {
        const context = canvas.getContext("2d");
        // Type assertions needed because TypeScript doesn't track mutations in traverse callback

        const texture = baseColorTexture as THREE.Texture;

        const geo = geometry as THREE.BufferGeometry;

        if (context && texture.image) {
          // Set canvas dimensions to match texture
          canvas.width = texture.image.width;
          canvas.height = texture.image.height;

          // Get UV coordinates and indices
          const uvs = geo.attributes.uv.array;
          const indices = geo.index ? geo.index.array : null;

          if (indices) {
            // Loop through each face of the mesh
            for (let i = 0; i < indices.length; i += 3) {
              // Get the vertex indices for each face
              const idx1 = indices[i] * 2;
              const idx2 = indices[i + 1] * 2;
              const idx3 = indices[i + 2] * 2;

              // Get UV coordinates for each face vertex.
              // A few notes:
              // - UV coordinates are in range [0,1], so scale them by the canvas width / height to
              //   place them accurately
              // - The UV array has a length of #vertices * 2, where the odd entries represent the X coordinates
              //   in UV space, and the even entries represent the Y coordinates
              const x1 = uvs[idx1] * canvas.width;
              const y1 = uvs[idx1 + 1] * canvas.height;
              const x2 = uvs[idx2] * canvas.width;
              const y2 = uvs[idx2 + 1] * canvas.height;
              const x3 = uvs[idx3] * canvas.width;
              const y3 = uvs[idx3 + 1] * canvas.height;

              // Draw triangle outline
              context.beginPath();
              context.moveTo(x1, y1);
              context.lineTo(x2, y2);
              context.lineTo(x3, y3);
              context.closePath();
              // Create a pattern from the texture image
              const pattern = context.createPattern(texture.image, "no-repeat");
              if (pattern) {
                context.fillStyle = pattern;

                // Save the current canvas state
                context.save();

                // Set up the pattern transform to match UV coordinates
                context.translate(0, 0);
                context.scale(1, 1);

                // Fill the triangle with the texture
                context.fill();

                // Restore the canvas state
                context.restore();
              }

              // Sketch wireframe in gray
              context.strokeStyle = "rgba(255, 255, 255, 0.2)";
              context.lineWidth = 0.5;
              context.stroke();
            }
          }
        }
      }
    });
  }, [url]);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.secondary[6]}`,
        borderRadius: "1px",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          aspectRatio: "1/1",
        }}
      />
    </Box>
  );
};
